/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { DatapointRecord, GenericInputParam, GenericOutputParam, NgGenericTask } from '@karya/core';
import { ComponentRenderer } from '@/components/TaskBuilder/Preview/ComponentRenderer';
import { parseComponent } from '@karya/generic-task-react';
import { useMemo } from 'react';
import { ComponentNode } from '@karya/generic-task-react';
import { Box } from '@chakra-ui/react';

const placeholderVoidFunc = () => {
  // Intentionally empty -- callbacks not needed in preview mode
};

export type ReportComponenetPreviewMeta = {
  type: string;
  data: DatapointRecord['data'];
  files: DatapointRecord['files'];
  component: NgGenericTask[string];
  fileUrlMap?: Record<string, string>;
};

export function ReportComponenetPreview(props: ReportComponenetPreviewMeta) {
  // Update param sources of the component to use the output datapoint
  const sourceAdjustedComponent = useMemo(() => {
    const updatedEntries = Object.entries(props.component).map(
      (entry: [string, GenericInputParam | GenericOutputParam | string]) => {
        if (typeof entry[1] == 'object' && 'src' in entry[1]) {
          entry[1] = { ...entry[1], src: 'MTA_OUTPUT' } as GenericInputParam;
        }
        return entry;
      },
    );
    return Object.fromEntries(updatedEntries) as NgGenericTask[string];
  }, [props.component]);

  const [parsedNode, error] = useMemo(() => {
    return parseComponent(sourceAdjustedComponent, {
      spec: {} as NgGenericTask,
      mtaOutput: {
        data: props.data as Record<string, unknown>,
        files: props.files,
      },
      taskParams: {},
      mtInput: { data: {} },
      fileUrlMap: props.fileUrlMap,
      workerProfile: {},
    })
      .map<ComponentNode>((params) => ({ params, component: sourceAdjustedComponent }))
      .toTuple();
  }, [sourceAdjustedComponent, props.data, props.files, props.fileUrlMap]);

  if (error) {
    return <Box> Component Parse Error: {error.code}</Box>;
  }

  return (
    <ComponentRenderer
      next={placeholderVoidFunc}
      writeOutput={placeholderVoidFunc}
      node={parsedNode}
      // Treat as non current component
      isCurrent={false}
      isDocked={false}
    />
  );
}
